import type { PortalAsset, PortalAssetReviewStatus } from '../../types/portalAsset.js';

// ONE ASSET'S REVIEW DECISION (commit 13, 2026-10-08). PURE — the server applies
// it in a transaction with the counter move and the audit entry.
//
// ⚠️ WHY A SINGLE-ASSET ROUTE EXISTS AT ALL: the spec puts approve/reject in
// `POST /bulk` (commit 10), which was deliberately left out of the overnight run.
// Without SOME review path a club's uploads go nowhere — upload → review →
// approved is the loop the staff surfaces exist for — so this is the smallest
// honest one. Bulk will route through `planReview` per asset rather than
// re-deciding the same rules.
//
// ⚠️ ONLY A READY, LIVE ASSET CAN BE REVIEWED. Approving something still
// processing approves a file nobody has seen; approving a failed one approves a
// file that does not exist as anything usable. Both are 409s that say why.
//
// ⚠️ A REJECTION CARRIES A REASON, FROM EITHER SIDE (2026-10-08: a club and Kyck
// are equal reviewers). Same field, different reader: Kyck's reason is read by
// the club, the club's by Kyck. "Not approved" with no reason teaches nobody
// anything.
//
// ⚠️ ONE RULE SET FOR BOTH ROUTES — the client review route calls the same
// reviewAsset, which calls this. Do not add a second planner for clubs.

export const REVIEW_REASON_MAX = 500;

export type ReviewDecisionInput = {
  decision: Exclude<PortalAssetReviewStatus, 'pending'>;
  reason?: string | null;
};

export type ReviewPlan =
  | { kind: 'refuse'; status: 400 | 409; message: string }
  | { kind: 'noop' }
  | { kind: 'apply'; status: Exclude<PortalAssetReviewStatus, 'pending'>; reason: string | null };

export function planReview(
  current: Pick<PortalAsset, 'lifecycle' | 'deletedAt' | 'review'>,
  input: ReviewDecisionInput,
): ReviewPlan {
  if (current.deletedAt !== null) {
    return { kind: 'refuse', status: 409, message: "A deleted asset can't be reviewed. Restore it first." };
  }
  if (current.lifecycle !== 'ready') {
    return {
      kind: 'refuse',
      status: 409,
      message:
        current.lifecycle === 'failed'
          ? "This file couldn't be processed, so there is nothing to review."
          : current.lifecycle === 'awaiting_support'
            ? "This video is still queued for processing, so it can't be reviewed yet."
            : "This file hasn't finished processing, so it can't be reviewed yet.",
    };
  }
  const reason = input.decision === 'rejected' ? (input.reason ?? '').trim() : '';
  if (input.decision === 'rejected' && reason === '') {
    return { kind: 'refuse', status: 400, message: 'Say why it isn’t approved — the reason is shown with the decision.' };
  }
  if (reason.length > REVIEW_REASON_MAX) {
    return { kind: 'refuse', status: 400, message: `A reason can be at most ${REVIEW_REASON_MAX} characters.` };
  }
  const nextReason = input.decision === 'rejected' ? reason : null;
  if (current.review.status === input.decision && (current.review.reason ?? null) === nextReason) {
    return { kind: 'noop' };
  }
  return { kind: 'apply', status: input.decision, reason: nextReason };
}
